export interface AnalysisRow {
  studyYearProgress: string; // e.g. "4" or "3 to 4"
  avgScoreDiff: number; // "progress %"
  daysGap: number;
  totalTimeFormatted: string; // "HH:MM:SS" practice time
  avgPreviousScore: number | null;
  avgLatestScore: number | null;
  noOfWLP: string; // "actual/expected"
  schoolName: string;
  fname: string;
}

// ---------- shared helpers ----------

function timeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const parts = timeStr.trim().split(":");
  if (parts.length !== 3) return 0;
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10) + parseInt(parts[2], 10) / 60;
}

function formattedTimeToHours(timeStr: string): number {
  if (!timeStr) return 0;
  const parts = timeStr.trim().split(":");
  if (parts.length !== 3) return 0;
  const hrs = parseInt(parts[0], 10) || 0;
  const mins = parseInt(parts[1], 10) || 0;
  const secs = parseInt(parts[2], 10) || 0;
  return hrs + mins / 60 + secs / 3600;
}

function average(arr: number[]): number {
  return arr.length === 0 ? 0 : arr.reduce((a, b) => a + b, 0) / arr.length;
}

function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function hoursToHHMMSS(decimalHours: number): string {
  if (!decimalHours || decimalHours <= 0) return "00:00:00";
  const totalSeconds = Math.round(decimalHours * 3600);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function getTimeBucket(hours: number): string {
  if (hours < 0.25) return "0-15m";
  if (hours < 0.5) return "15-30m";
  if (hours < 0.75) return "30-45m";
  if (hours < 1.0) return "45-60m";
  return "60m+";
}

function getImprovementBucket(improvement: number): string {
  if (improvement < 5) return "0-5%";
  if (improvement < 10) return "5-10%";
  if (improvement < 15) return "10-15%";
  if (improvement < 20) return "15-20%";
  if (improvement < 30) return "20-30%";
  return "30%+";
}

function parsePracticeTimeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const parts = timeStr.trim().split(":");
  if (parts.length === 3) {
    const h = parseFloat(parts[0]) || 0;
    const m = parseFloat(parts[1]) || 0;
    const s = parseFloat(parts[2]) || 0;
    return h * 60 + m + s / 60;
  } else if (parts.length === 2) {
    const m = parseFloat(parts[0]) || 0;
    const s = parseFloat(parts[1]) || 0;
    return m + s / 60;
  }
  return parseFloat(timeStr) || 0;
}

function formatMinutesToReadable(totalMinutes: number): string {
  if (!totalMinutes || isNaN(totalMinutes) || totalMinutes <= 0) return "0m";
  const mins = Math.round(totalMinutes);
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (hours > 0) return `${hours}h ${remainingMins > 0 ? remainingMins + "m" : ""}`;
  return `${remainingMins}m`;
}

function formatProgressText(val: number | string): string {
  const num = parseFloat(String(val));
  return num >= 0 ? `+${num}%` : `${num}%`;
}

function latestGradeFrom(studyYearText: string): number {
  const text = (studyYearText || "").trim();
  if (text.includes("to")) {
    const parts = text.split("to");
    return parseInt(parts[1].trim(), 10);
  }
  return parseInt(text, 10);
}

const GRADE_COLORS: Record<string, string> = {
  "1": "rgba(75,192,192,0.8)",
  "2": "rgba(54,162,235,0.8)",
  "3": "rgba(255,206,86,0.8)",
  "4": "rgba(255,159,64,0.8)",
  "5": "rgba(153,102,255,0.8)",
  "6": "rgba(255,99,132,0.8)",
  "7": "rgba(46,204,113,0.8)",
  "8": "rgba(52,152,219,0.8)",
  "9": "rgba(241,196,15,0.8)",
  "10": "rgba(231,76,60,0.8)",
};

// Same lazy-load guard as the original: inject Chart.js + the datalabels
// plugin from CDN once, reuse thereafter.
function loadChartJs(onReady: () => void) {
  const w = window as any;
  if (typeof w.Chart === "undefined") {
    const scriptTag = document.createElement("script");
    scriptTag.src = "https://cdn.jsdelivr.net/npm/chart.js";
    scriptTag.onload = () => {
      const scriptLabel = document.createElement("script");
      scriptLabel.src = "https://cdn.jsdelivr.net/npm/chartjs-plugin-datalabels";
      scriptLabel.onload = () => {
        w.Chart.register(w.ChartDataLabels);
        onReady();
      };
      document.head.appendChild(scriptLabel);
    };
    document.head.appendChild(scriptTag);
  } else if (typeof w.ChartDataLabels === "undefined") {
    const scriptLabel = document.createElement("script");
    scriptLabel.src = "https://cdn.jsdelivr.net/npm/chartjs-plugin-datalabels";
    scriptLabel.onload = () => {
      w.Chart.register(w.ChartDataLabels);
      onReady();
    };
    document.head.appendChild(scriptLabel);
  } else {
    w.Chart.register(w.ChartDataLabels);
    onReady();
  }
}

function makeCriteriaPopup(counterBox: HTMLElement, html: string) {
  const criteriaLink = document.createElement("div");
  criteriaLink.innerHTML = "ⓘ <span style='text-decoration:underline;cursor:pointer;'>Analysis Criteria</span>";
  criteriaLink.style.marginTop = "8px";
  criteriaLink.style.color = "#0066cc";
  criteriaLink.style.fontWeight = "bold";
  criteriaLink.style.cursor = "pointer";

  const criteriaPopup = document.createElement("div");
  criteriaPopup.style.position = "absolute";
  criteriaPopup.style.top = "100%";
  criteriaPopup.style.right = "0";
  criteriaPopup.style.marginTop = "2px";
  criteriaPopup.style.width = "360px";
  criteriaPopup.style.padding = "12px";
  criteriaPopup.style.background = "#ffffff";
  criteriaPopup.style.border = "1px solid #ccc";
  criteriaPopup.style.borderRadius = "6px";
  criteriaPopup.style.boxShadow = "0 4px 12px rgba(0,0,0,.25)";
  criteriaPopup.style.fontSize = "12px";
  criteriaPopup.style.lineHeight = "1.6";
  criteriaPopup.style.display = "none";
  criteriaPopup.innerHTML = html;

  counterBox.style.position = "relative";
  counterBox.onmouseenter = () => (criteriaPopup.style.display = "block");
  counterBox.onmouseleave = () => (criteriaPopup.style.display = "none");
  counterBox.appendChild(criteriaLink);
  counterBox.appendChild(criteriaPopup);
}

// ---------- 1. View Chart: median improvement by weekly practice bracket ----------

export function analyzeAllBracketsAndDrawChart(rows: AnalysisRow[]) {
  interface Bracket {
    label: string;
    min: number;
    max: number;
    scores: number[];
  }
  const createBrackets = (): Bracket[] => [
    { label: "0-30m", min: 0, max: 0.5, scores: [] },
    { label: "30m-45m", min: 0.5, max: 0.75, scores: [] },
    { label: "45m-1h", min: 0.75, max: 1.0, scores: [] },
    { label: "1h-1.25h", min: 1.0, max: 1.25, scores: [] },
    { label: "1.25h-1.5h", min: 1.25, max: 1.5, scores: [] },
    { label: "1.5h+", min: 1.5, max: Infinity, scores: [] },
  ];

  const gradeGroups: Record<string, Bracket[]> = {};
  for (let i = 1; i <= 10; i++) gradeGroups[i] = createBrackets();

  const totalGradeCounts: Record<string, number> = {};
  const filteredGradeCounts: Record<string, number> = {};
  for (let i = 1; i <= 10; i++) {
    totalGradeCounts[i] = 0;
    filteredGradeCounts[i] = 0;
  }

  // 1. Unconditional totals.
  rows.forEach((row) => {
    const grade = latestGradeFrom(row.studyYearProgress);
    if (!isNaN(grade) && grade >= 1 && grade <= 10) totalGradeCounts[grade]++;
  });

  // 2. Filtered bracket assignment.
  rows.forEach((row) => {
    const progress = row.avgScoreDiff;
    const daysGap = row.daysGap;
    const practiceMinutes = timeToMinutes(row.totalTimeFormatted);
    const previousScore = row.avgPreviousScore;

    if (isNaN(progress) || isNaN(daysGap) || daysGap <= 0 || practiceMinutes <= 0) return;

    const weeklyHours = practiceMinutes / 60 / (daysGap / 7);
    if (daysGap < 42 || daysGap > 365) return;
    if (progress < 0) return;
    if (previousScore === null || isNaN(previousScore)) return;
    if (previousScore < 20 || previousScore > 85) return;

    const grade = latestGradeFrom(row.studyYearProgress);
    if (isNaN(grade) || grade < 1 || grade > 10) return;

    const targetBrackets = gradeGroups[grade];
    for (const bracket of targetBrackets) {
      if (weeklyHours >= bracket.min && weeklyHours < bracket.max) {
        bracket.scores.push(progress);
        filteredGradeCounts[grade]++;
        break;
      }
    }
  });

  const timeLabels = ["0-30m", "30m-45m", "45m-1h", "1h-1.25h", "1.25h-1.5h", "1.5h+"];
  const datasets: any[] = [];

  Object.keys(gradeGroups).forEach((grade) => {
    const gradeDataPoints: (number | null)[] = [];
    const barStudentCounts: number[] = [];
    let maxImp = -1;
    let winningLabel = "No Data";

    gradeGroups[grade].forEach((b) => {
      if (b.scores.length < 10) return;
      const avg = median(b.scores);
      if (avg > maxImp) {
        maxImp = avg;
        winningLabel = b.label;
      }
    });

    gradeGroups[grade].forEach((b) => {
      if (b.scores.length < 10) {
        gradeDataPoints.push(null);
        barStudentCounts.push(b.scores.length);
        return;
      }
      gradeDataPoints.push(parseFloat(median(b.scores).toFixed(2)));
      barStudentCounts.push(b.scores.length);
    });

    const hasData = gradeDataPoints.some((x) => x !== null);
    if (!hasData) return;

    datasets.push({
      label: `Grade ${grade} (🏆 Best: ${winningLabel})`,
      data: gradeDataPoints,
      studentCounts: barStudentCounts,
      backgroundColor: GRADE_COLORS[grade],
      borderColor: GRADE_COLORS[grade].replace("0.8", "1"),
      borderWidth: 1,
      barPercentage: 0.55,
      categoryPercentage: 0.65,
    });
  });

  loadChartJs(() => renderMultiBarChart(timeLabels, datasets, totalGradeCounts, filteredGradeCounts));
}

function renderMultiBarChart(
  labels: string[],
  structuredDatasets: any[],
  totalGradeCounts: Record<string, number>,
  filteredGradeCounts: Record<string, number>
) {
  const Chart = (window as any).Chart;

  document.getElementById("mathswiz-all-brackets-container")?.remove();

  const container = document.createElement("div");
  container.id = "mathswiz-all-brackets-container";
  Object.assign(container.style, {
    position: "fixed",
    top: "5%",
    left: "15%",
    width: "70%",
    backgroundColor: "#ffffff",
    zIndex: "999999",
    padding: "30px",
    boxShadow: "0px 10px 40px rgba(0,0,0,0.4)",
    borderRadius: "12px",
    fontFamily: "Arial, sans-serif",
  });

  const closeBtn = document.createElement("button");
  closeBtn.innerText = "✕ Close";
  Object.assign(closeBtn.style, {
    float: "right",
    cursor: "pointer",
    border: "none",
    backgroundColor: "#f44336",
    color: "white",
    padding: "8px 16px",
    borderRadius: "4px",
    fontWeight: "bold",
  });
  closeBtn.onclick = () => container.remove();

  const counterBox = document.createElement("div");
  Object.assign(counterBox.style, {
    float: "right",
    marginRight: "20px",
    padding: "10px 14px",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e9ecef",
    borderRadius: "6px",
    fontSize: "12px",
    lineHeight: "1.5",
    color: "#333333",
  });

  let html = "<b>Student Metric Overview (Total Row Count vs Active Chart Count)</b><br>";
  for (let i = 1; i <= 10; i++) {
    if (totalGradeCounts[i] > 0) {
      html += `<strong>Grade ${i}:</strong> ${totalGradeCounts[i]} total rows (${filteredGradeCounts[i]} in chart)<br>`;
    }
  }
  counterBox.innerHTML = html;

  makeCriteriaPopup(
    counterBox,
    "<b>Analysis Criteria</b><br>" +
      "• Grade Groups: Grades 1, 2, 3, 4, 5, 6, 7, 8, 9, 10<br>" +
      "• Weekly Practice = Total Practice Time ÷ (Days Between Tests ÷ 7)<br>" +
      "• Test Gap: 42–365 days<br>" +
      "• Previous Score: 20%–85%<br>" +
      "• Improvement ≥ 0%<br>" +
      "• Practice Time > 0<br><br>" +
      "• Only practice-time brackets containing at least <b>10 students</b> are displayed.<br><br>" +
      "<b>Chart Interpretation</b><br>" +
      "Each bar represents the median improvement (%) for students within a weekly practice-time range. " +
      "The highest median improvement for each grade group is identified as the <b>Best Practice Range</b>."
  );

  const canvas = document.createElement("canvas");
  container.appendChild(closeBtn);
  const title = document.createElement("h3");
  title.innerText = "Weekly Practice Required To Achieve Improvement";
  title.style.marginBottom = "15px";
  container.appendChild(title);
  container.appendChild(counterBox);
  container.appendChild(canvas);
  document.body.appendChild(container);

  new Chart(canvas.getContext("2d"), {
    type: "bar",
    data: { labels, datasets: structuredDatasets },
    options: {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: "Median Improvement by Weekly Practice Time",
          font: { size: 18, weight: "bold" },
        },
        legend: { position: "bottom", labels: { padding: 15, font: { size: 12, weight: "bold" } } },
        datalabels: {
          anchor: "end",
          align: "top",
          color: "#000",
          font: { weight: "bold", size: 11 },
          formatter: (value: number | null, context: any) => {
            if (value == null) return "";
            const count = context.dataset.studentCounts[context.dataIndex];
            return `${value}% (${count})`;
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const value = context.parsed.y;
              const count = context.dataset.studentCounts[context.dataIndex];
              return `${context.dataset.label.split(" (")[0]}: ${value}% Improvement (${count})`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: { display: true, text: "Average Score Improvement (%)", font: { weight: "bold" } },
        },
        x: { title: { display: true, text: "Time Spent Practicing Per Week", font: { weight: "bold" } } },
      },
    },
  });
}

// ---------- 2. Improvement: weekly practice required per improvement bracket ----------

export function analyzeWeeklyPracticeRequired(rows: AnalysisRow[]) {
  interface Bucket {
    label: string;
    min: number;
    max: number;
    weeklyHours: number[];
    studentCount: number;
  }
  const createBuckets = (): Bucket[] => [
    { label: "0-5%", min: 0, max: 5, weeklyHours: [], studentCount: 0 },
    { label: "5-10%", min: 5, max: 10, weeklyHours: [], studentCount: 0 },
    { label: "10-15%", min: 10, max: 15, weeklyHours: [], studentCount: 0 },
    { label: "15-20%", min: 15, max: 20, weeklyHours: [], studentCount: 0 },
    { label: "20-30%", min: 20, max: 30, weeklyHours: [], studentCount: 0 },
    { label: "30%+", min: 30, max: Infinity, weeklyHours: [], studentCount: 0 },
  ];

  const totalGradeCounts: Record<string, number> = {};
  const filteredGradeCounts: Record<string, number> = {};
  for (let i = 1; i <= 10; i++) {
    totalGradeCounts[i] = 0;
    filteredGradeCounts[i] = 0;
  }

  const gradeGroups: Record<string, Bucket[]> = {};
  for (let i = 1; i <= 10; i++) gradeGroups[i] = createBuckets();

  rows.forEach((row) => {
    const grade = latestGradeFrom(row.studyYearProgress);
    if (isNaN(grade) || grade < 1 || grade > 10) return;

    totalGradeCounts[grade]++;

    const daysGap = row.daysGap;
    if (daysGap < 42 || daysGap > 365) return;

    const totalHours = formattedTimeToHours(row.totalTimeFormatted);
    if (totalHours <= 0 || daysGap <= 0) return;

    const weeklyHours = totalHours / (daysGap / 7);
    const avgScoreDiff = row.avgScoreDiff;
    const previousScore = row.avgPreviousScore;

    if (avgScoreDiff < 0) return;
    if (previousScore === null || isNaN(previousScore)) return;
    if (previousScore < 20 || previousScore > 85) return;

    const targetBuckets = gradeGroups[grade];
    for (const bucket of targetBuckets) {
      if (avgScoreDiff >= bucket.min && avgScoreDiff < bucket.max) {
        bucket.weeklyHours.push(weeklyHours);
        bucket.studentCount++;
        filteredGradeCounts[grade]++;
        break;
      }
    }
  });

  const chartLabels = ["0-5%", "5-10%", "10-15%", "15-20%", "20-30%", "30%+"];
  const datasets: any[] = [];

  Object.keys(gradeGroups).forEach((grade) => {
    const dataAverages: (number | null)[] = [];
    const tooltipsInfo: number[] = [];

    gradeGroups[grade].forEach((bucket) => {
      if (bucket.studentCount < 10) {
        dataAverages.push(null);
        tooltipsInfo.push(bucket.studentCount);
        return;
      }
      const med = median(bucket.weeklyHours);
      dataAverages.push(parseFloat(med.toFixed(2)));
      tooltipsInfo.push(bucket.studentCount);
    });

    const hasData = dataAverages.some((x) => x !== null);
    if (!hasData) return;

    datasets.push({
      label: `Grade ${grade}`,
      data: dataAverages,
      backgroundColor: GRADE_COLORS[grade],
      borderColor: GRADE_COLORS[grade].replace("0.7", "1"),
      borderWidth: 1,
      barPercentage: 0.55,
      categoryPercentage: 0.65,
      studentCounts: tooltipsInfo,
    });
  });

  loadChartJs(() =>
    renderImprovementChart(chartLabels, datasets, totalGradeCounts, filteredGradeCounts)
  );
}

function renderImprovementChart(
  labels: string[],
  datasets: any[],
  totalGradeCounts: Record<string, number>,
  filteredGradeCounts: Record<string, number>
) {
  const Chart = (window as any).Chart;

  document.getElementById("weekly-practice-container")?.remove();

  const container = document.createElement("div");
  container.id = "weekly-practice-container";
  Object.assign(container.style, {
    position: "fixed",
    top: "5%",
    left: "15%",
    width: "70%",
    height: "70%",
    backgroundColor: "#ffffff",
    zIndex: "999999",
    padding: "30px",
    boxShadow: "0px 10px 40px rgba(0,0,0,0.4)",
    borderRadius: "12px",
  });

  const closeBtn = document.createElement("button");
  closeBtn.innerText = "✕ Close";
  Object.assign(closeBtn.style, {
    float: "right",
    cursor: "pointer",
    border: "none",
    backgroundColor: "#f44336",
    color: "white",
    padding: "8px 16px",
    borderRadius: "4px",
    fontWeight: "bold",
  });
  closeBtn.onclick = () => container.remove();
  container.appendChild(closeBtn);

  const counterBox = document.createElement("div");
  Object.assign(counterBox.style, {
    float: "right",
    marginRight: "20px",
    padding: "10px",
    background: "#f8f9fa",
    border: "1px solid #ddd",
    borderRadius: "6px",
  });

  let html = "<b>Student Metric Overview (Total Row Count vs Active Chart Count)</b><br>";
  for (let i = 1; i <= 10; i++) {
    if (totalGradeCounts[i] > 0) {
      html += `<strong>Grade ${i}:</strong> ${totalGradeCounts[i]} total rows (${filteredGradeCounts[i]} in chart)<br>`;
    }
  }
  counterBox.innerHTML = html;

  makeCriteriaPopup(
    counterBox,
    "<b>Analysis Criteria</b><br>" +
      "• Grades: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10<br>" +
      "• Weekly Practice = Total Practice Time ÷ (Days Between Tests ÷ 7)<br>" +
      "• Test Gap: 42–365 days<br>" +
      "• Previous Score: 20%–85%<br>" +
      "• Improvement ≥ 0%<br>" +
      "• Practice Time > 0<br><br>" +
      "• Only improvement brackets containing at least <b>10 students</b> are displayed.<br><br>" +
      "<b>Chart Interpretation</b><br>" +
      "Each bar represents the <b>median weekly practice time</b> required for students who achieved the specified improvement range."
  );

  container.appendChild(counterBox);

  const canvas = document.createElement("canvas");
  canvas.style.width = "100%";
  canvas.style.height = "70%";
  container.appendChild(canvas);
  document.body.appendChild(container);

  const ctx = canvas.getContext("2d");
  const w = window as any;
  if (w.myCurrentChartInstance) w.myCurrentChartInstance.destroy();

  w.myCurrentChartInstance = new Chart(ctx, {
    type: "bar",
    data: { labels, datasets },
    options: {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: "Weekly Practice Required to Match Target Improvement",
          font: { size: 16, weight: "bold" },
        },
        datalabels: {
          anchor: "end",
          align: "top",
          color: "#000",
          font: { weight: "bold", size: 10 },
          formatter: (value: number | null, context: any) => {
            if (value == null) return "";
            const count = context.dataset.studentCounts[context.dataIndex];
            return [hoursToHHMMSS(value), `(${count})`];
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const dataset = context.dataset;
              const hoursValue = context.raw;
              const count = dataset.studentCounts[context.dataIndex];
              return `${dataset.label}: ${hoursToHHMMSS(hoursValue)} / week (${count} students)`;
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: "Improvement Target (AvgScoreDiff)" } },
        y: {
          title: { display: true, text: "Typical Practice Time Per Week" },
          beginAtZero: true,
          ticks: { callback: (value: number) => hoursToHHMMSS(value) },
        },
      },
    },
  });
}

// ---------- 3. Time vs Improvement Matrix ----------

export function analyzeTimeImprovementMatrix(rows: AnalysisRow[]) {
  const totalGradeCounts: Record<string, number> = {};
  const filteredGradeCounts: Record<string, number> = {};
  for (let i = 1; i <= 10; i++) {
    totalGradeCounts[i] = 0;
    filteredGradeCounts[i] = 0;
  }

  const timeBuckets = ["0-15m", "15-30m", "30-45m", "45-60m", "60m+"];
  const improvementBuckets = ["0-5%", "5-10%", "10-15%", "15-20%", "20-30%", "30%+"];

  const matrix: Record<string, Record<string, Record<string, number>>> = {};
  for (let i = 1; i <= 10; i++) {
    matrix[i] = {};
    timeBuckets.forEach((t) => {
      matrix[i][t] = {};
      improvementBuckets.forEach((imp) => (matrix[i][t][imp] = 0));
    });
  }

  rows.forEach((row) => {
    const grade = latestGradeFrom(row.studyYearProgress);
    if (isNaN(grade) || grade < 1 || grade > 10) return;

    totalGradeCounts[grade]++;

    const totalHours = formattedTimeToHours(row.totalTimeFormatted);
    if (totalHours <= 0) return;

    const weeklyHours = totalHours / (row.daysGap / 7);
    const timeBucket = getTimeBucket(weeklyHours);
    const improvementBucket = getImprovementBucket(row.avgScoreDiff);

    filteredGradeCounts[grade]++;
    matrix[grade][timeBucket][improvementBucket]++;
  });

  renderMatrixTable(matrix, timeBuckets, improvementBuckets, totalGradeCounts, filteredGradeCounts);
}

function renderMatrixTable(
  matrix: Record<string, Record<string, Record<string, number>>>,
  timeBuckets: string[],
  improvementBuckets: string[],
  totalGradeCounts: Record<string, number>,
  filteredGradeCounts: Record<string, number>
) {
  document.getElementById("matrixContainer")?.remove();

  const container = document.createElement("div");
  container.id = "matrixContainer";
  container.style.position = "fixed";

  let visibleGrades = 0;
  Object.keys(totalGradeCounts).forEach((g) => {
    if (totalGradeCounts[g] > 0) visibleGrades++;
  });

  container.style.top = "3%";
  if (visibleGrades === 1) {
    container.style.width = "40%";
    container.style.height = "83%";
    container.style.left = "30%";
  } else if (visibleGrades === 2) {
    container.style.width = "78%";
    container.style.height = "82%";
    container.style.left = "11%";
  } else {
    container.style.width = "94%";
    container.style.height = "92%";
    container.style.left = "3%";
  }
  Object.assign(container.style, {
    background: "#f5f7fb",
    borderRadius: "14px",
    boxShadow: "0 12px 35px rgba(0,0,0,.25)",
    zIndex: "999999",
    overflow: "auto",
    resize: "both",
    fontFamily: "'Segoe UI',Arial",
    cursor: "move",
  });

  let totalStudents = 0;
  let filteredStudents = 0;
  Object.keys(totalGradeCounts).forEach((g) => {
    totalStudents += totalGradeCounts[g];
    filteredStudents += filteredGradeCounts[g];
  });

  let html = "";
  html +=
    "<div id='matrixHeader' style='position:sticky;top:0;z-index:100;background:#0f4c81;color:#fff;padding:18px 24px;border-radius:14px 14px 0 0;'>";
  html +=
    "<button id='matrixCloseBtn' style='float:right;background:#d32f2f;color:#fff;border:none;padding:10px 18px;border-radius:6px;font-weight:bold;cursor:pointer;'>✕ Close</button>";
  html += "<div id='matrixCriteriaHolder' style='float:right;margin-right:15px;position:relative;'></div>";
  html += "<div style='font-size:26px;font-weight:700;'>Time vs Improvement Matrix</div>";
  html +=
    "<div style='opacity:.85;font-size:13px;margin-top:4px;'>Weekly Practice Time vs Average Improvement Distribution</div>";
  html += "</div>";
  html += "<div style='padding:12px;'>";

  if (visibleGrades > 1) {
    html += "<div style='display:flex;gap:15px;flex-wrap:wrap;margin-bottom:22px;'>";
    html +=
      `<div style='flex:1;min-width:180px;background:#fff;border-radius:10px;padding:16px;box-shadow:0 2px 10px rgba(0,0,0,.08);'>` +
      `<div style='font-size:13px;color:#777;'>Total Students</div>` +
      `<div style='font-size:30px;font-weight:bold;color:#0f4c81;margin-top:6px;'>${totalStudents}</div></div>`;
    html +=
      `<div style='flex:1;min-width:180px;background:#fff;border-radius:10px;padding:16px;box-shadow:0 2px 10px rgba(0,0,0,.08);'>` +
      `<div style='font-size:13px;color:#777;'>Students After Filters</div>` +
      `<div style='font-size:30px;font-weight:bold;color:#2e7d32;margin-top:6px;'>${filteredStudents}</div></div>`;
    html +=
      `<div style='flex:1;min-width:180px;background:#fff;border-radius:10px;padding:16px;box-shadow:0 2px 10px rgba(0,0,0,.08);'>` +
      `<div style='font-size:13px;color:#777;'>Test Gap</div>` +
      `<div style='font-size:22px;font-weight:bold;color:#ff9800;margin-top:8px;'>42 - 365 Days</div></div>`;
    html +=
      `<div style='flex:1;min-width:180px;background:#fff;border-radius:10px;padding:16px;box-shadow:0 2px 10px rgba(0,0,0,.08);'>` +
      `<div style='font-size:13px;color:#777;'>Previous Score</div>` +
      `<div style='font-size:22px;font-weight:bold;color:#9c27b0;margin-top:8px;'>20% - 85%</div></div>`;
    html += "</div>";
  }

  const gridCols = visibleGrades === 1 ? "1fr" : "repeat(auto-fit,minmax(420px,1fr))";
  html += `<div style='display:grid;grid-template-columns:${gridCols};gap:22px;'>`;

  Object.keys(matrix).forEach((grade) => {
    if (totalGradeCounts[grade] === 0) return;

    html += "<div style='background:#fff;border-radius:12px;padding:18px;box-shadow:0 2px 12px rgba(0,0,0,.08);'>";
    html += "<div style='display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;'>";
    html += `<div style='font-size:22px;font-weight:700;color:#0f4c81;'>Grade ${grade}</div>`;
    html +=
      `<div style='background:#e8f2ff;color:#0f4c81;padding:6px 12px;border-radius:25px;font-weight:bold;'>` +
      `${filteredGradeCounts[grade]} / ${totalGradeCounts[grade]} Students</div>`;
    html += "</div>";

    html += "<table style='width:100%;border-collapse:collapse;font-size:13px;'><thead>";
    html += "<tr style='background:#0f4c81;color:#fff;'><th style='padding:10px;border:1px solid #d6d6d6;'>Practice Time</th>";
    improvementBuckets.forEach((b) => (html += `<th style='padding:10px;border:1px solid #d6d6d6;'>${b}</th>`));
    html += "</tr></thead><tbody>";

    timeBuckets.forEach((t) => {
      html += "<tr>";
      html += `<td style='padding:10px;font-weight:600;background:#f8f9fc;border:1px solid #ddd;'>${t}</td>`;
      improvementBuckets.forEach((imp) => {
        const value = matrix[grade][t][imp];
        let bg = "#ffffff";
        let color = "#333";
        if (value >= 100) {
          bg = "#d73027";
          color = "#fff";
        } else if (value >= 75) bg = "#fc8d59";
        else if (value >= 50) bg = "#fee08b";
        else if (value >= 25) bg = "#d9ef8b";
        else if (value >= 10) bg = "#91cf60";
        else if (value > 0) bg = "#e8f5e9";

        html += `<td style='padding:10px;text-align:center;font-weight:bold;border:1px solid #ddd;background:${bg};color:${color};transition:.2s;'>${value}</td>`;
      });
      html += "</tr>";
    });

    html += "</tbody></table></div>";
  });

  html += "</div>";

  html += "<div style='margin-top:30px;background:#fff;border-radius:12px;padding:20px;box-shadow:0 2px 12px rgba(0,0,0,.08);'>";
  html += "<div style='font-size:22px;font-weight:700;color:#0f4c81;margin-bottom:15px;'>How to Read This Matrix</div>";
  html += "<div style='display:flex;gap:40px;flex-wrap:wrap;'>";
  html += "<div style='flex:2;min-width:300px;'><ul style='margin:0;padding-left:18px;line-height:2;'>";
  html += "<li><b>Rows</b> represent Weekly Practice Time.</li>";
  html += "<li><b>Columns</b> represent Improvement Percentage.</li>";
  html += "<li>Each cell shows the number of students in that category.</li>";
  html += "<li>Darker colors indicate more students.</li>";
  html += "<li>Compare rows to identify the practice range that produces better improvements.</li>";
  html += "</ul></div>";

  html += "<div style='flex:1;min-width:220px;'><div style='font-weight:bold;margin-bottom:10px;'>Heat Map Legend</div>";
  const legend = (color: string, text: string) => {
    html +=
      `<div style='display:flex;align-items:center;margin-bottom:8px;'>` +
      `<div style='width:22px;height:22px;background:${color};border:1px solid #999;margin-right:10px;border-radius:4px;'></div>${text}</div>`;
  };
  legend("#ffffff", "0");
  legend("#e8f5e9", "1 - 9");
  legend("#91cf60", "10 - 24");
  legend("#d9ef8b", "25 - 49");
  legend("#fee08b", "50 - 74");
  legend("#fc8d59", "75 - 99");
  legend("#d73027", "100+");
  html += "</div></div></div></div>";

  container.innerHTML = html;
  document.body.appendChild(container);

  const holder = document.getElementById("matrixCriteriaHolder")!;
  const criteriaLink = document.createElement("div");
  criteriaLink.innerHTML = "ⓘ <span style='text-decoration:underline;cursor:pointer;'>Analysis Criteria</span>";
  Object.assign(criteriaLink.style, {
    display: "inline-block",
    padding: "8px 14px",
    background: "#ffffff",
    color: "#0f4c81",
    borderRadius: "6px",
    fontWeight: "600",
    cursor: "pointer",
    border: "1px solid #d0d7e2",
  });

  const criteriaPopup = document.createElement("div");
  Object.assign(criteriaPopup.style, {
    position: "absolute",
    top: "45px",
    right: "0",
    width: "420px",
    padding: "18px",
    background: "#ffffff",
    border: "1px solid #ddd",
    borderRadius: "10px",
    boxShadow: "0 8px 25px rgba(0,0,0,.25)",
    fontSize: "13px",
    lineHeight: "1.8",
    display: "none",
    color: "#444",
  });
  criteriaPopup.innerHTML =
    "<div style='font-size:18px;font-weight:700;color:#0f4c81;margin-bottom:10px;'>Analysis Criteria</div>" +
    "<b>Grades</b><br>1, 2, 3, 4, 5, 6, 7, 8, 9, 10<br><br>" +
    "<b>Weekly Practice Formula</b><br>Total Practice Time ÷ (Days Between Tests ÷ 7)<br><br>" +
    "<b>Filters</b><br>• Practice Time > 0<br><br>" +
    "<b>Interpretation</b><br>Each cell shows the number of students whose weekly practice time falls into the corresponding practice-time bucket and improvement bucket.";

  holder.appendChild(criteriaLink);
  holder.appendChild(criteriaPopup);
  holder.onmouseenter = () => (criteriaPopup.style.display = "block");
  holder.onmouseleave = () => (criteriaPopup.style.display = "none");

  const header = document.getElementById("matrixHeader")!;
  let isDragging = false;
  let offsetX = 0;
  let offsetY = 0;

  header.addEventListener("mousedown", (e) => {
    isDragging = true;
    offsetX = e.clientX - container.offsetLeft;
    offsetY = e.clientY - container.offsetTop;
    document.body.style.userSelect = "none";
  });
  document.addEventListener("mousemove", (e) => {
    if (!isDragging) return;
    container.style.left = `${e.clientX - offsetX}px`;
    container.style.top = `${e.clientY - offsetY}px`;
  });
  document.addEventListener("mouseup", () => {
    isDragging = false;
    document.body.style.userSelect = "";
  });

  document.getElementById("matrixCloseBtn")!.onclick = () => container.remove();
}

// ---------- 4. WLP Completion Summary (modal: pie + scatter + table) ----------

interface WlpStudent {
  name: string;
  school: string;
  wlp: string;
  practiceText: string;
  prevAvg: number;
  latestAvg: number;
  progress: number;
  daysGap: number;
  weeklyPracticeTime: number;
  bracketKey: string;
}

interface WlpGroup {
  count: number;
  progressSum: number;
  progressCount: number;
  students: WlpStudent[];
  label: string;
  color: string;
  key: string;
  avgProgress?: string;
  percent?: string;
}

export function analyzeWLPCompletion(rows: AnalysisRow[], studyYearLabel: string) {
  const globalGroups: Record<string, WlpGroup> = {
    "90": { count: 0, progressSum: 0, progressCount: 0, students: [], label: "90–100% Completed", color: "#2ecc71", key: "90" },
    "75": { count: 0, progressSum: 0, progressCount: 0, students: [], label: "75–89% Completed", color: "#3498db", key: "75" },
    "50": { count: 0, progressSum: 0, progressCount: 0, students: [], label: "50–74% Completed", color: "#f39c12", key: "50" },
    "25": { count: 0, progressSum: 0, progressCount: 0, students: [], label: "25–49% Completed", color: "#e74c3c", key: "25" },
    "0": { count: 0, progressSum: 0, progressCount: 0, students: [], label: "0–24% Completed", color: "#9b59b6", key: "0" },
  };

  const wlpDataStore: WlpStudent[] = [];

  rows.forEach((row) => {
    const wlpText = row.noOfWLP || "";
    if (!wlpText || wlpText.indexOf("/") === -1) return;

    const parts = wlpText.split("/");
    const actual = parseInt(parts[0], 10);
    const expected = parseInt(parts[1], 10);
    if (isNaN(actual) || isNaN(expected) || expected <= 0) return;

    const percent = (actual / expected) * 100;
    const prevAvg = row.avgPreviousScore ?? 0;
    const latestAvg = row.avgLatestScore ?? 0;
    const progress = parseFloat((latestAvg - prevAvg).toFixed(2));
    const daysGap = row.daysGap || 0;
    const timeTakenMins = parsePracticeTimeToMinutes(row.totalTimeFormatted);
    const weeklyPracticeTime = daysGap > 0 ? (timeTakenMins / daysGap) * 7 : 0;

    let key = "0";
    if (percent >= 90) key = "90";
    else if (percent >= 75) key = "75";
    else if (percent >= 50) key = "50";
    else if (percent >= 25) key = "25";

    const studentObj: WlpStudent = {
      name: row.fname || "N/A",
      school: row.schoolName || "N/A",
      wlp: wlpText,
      practiceText: row.totalTimeFormatted,
      prevAvg,
      latestAvg,
      progress,
      daysGap,
      weeklyPracticeTime: Math.round(weeklyPracticeTime * 10) / 10,
      bracketKey: key,
    };

    globalGroups[key].count++;
    if (progress > 0) {
      globalGroups[key].progressSum += progress;
      globalGroups[key].progressCount++;
    }
    globalGroups[key].students.push(studentObj);
    wlpDataStore.push(studentObj);
  });

  const totalStudents = wlpDataStore.length;
  if (totalStudents === 0) {
    alert("No valid student WLP records found.");
    return;
  }

  Object.keys(globalGroups).forEach((k) => {
    const g = globalGroups[k];
    g.avgProgress = g.progressCount > 0 ? (g.progressSum / g.progressCount).toFixed(1) : "0.0";
    g.percent = ((g.count / totalStudents) * 100).toFixed(2);
  });

  const partialCount = globalGroups["75"].count + globalGroups["50"].count + globalGroups["25"].count;
  const partialAvgProgress =
    partialCount > 0
      ? (
          (globalGroups["75"].progressSum + globalGroups["50"].progressSum + globalGroups["25"].progressSum) /
          partialCount
        ).toFixed(1)
      : "0.0";

  renderWlpModal(globalGroups, totalStudents, partialCount, partialAvgProgress, studyYearLabel);
}

function renderSummaryRow(group: WlpGroup): string {
  const pNum = parseFloat(group.avgProgress || "0");
  const pColor = pNum > 0 ? "#27ae60" : pNum < 0 ? "#e74c3c" : "gray";
  return `
    <tr style="border-bottom:1px solid #eee;">
      <td style="text-align:left; padding:6px;">
        <span style="display:inline-block;width:14px;height:14px;background:${group.color};border:1px solid rgba(0,0,0,.25);box-shadow:0 1px 2px rgba(0,0,0,.25);border-radius:2px;margin-right:8px;vertical-align:middle;"></span>
        ${group.label}
      </td>
      <td>${group.count}</td>
      <td>${group.percent}%</td>
      <td style="color:${pColor}; font-weight:bold;">${formatProgressText(group.avgProgress || "0")}</td>
    </tr>
  `;
}

function renderWlpModal(
  globalGroups: Record<string, WlpGroup>,
  totalStudents: number,
  partialCount: number,
  partialAvgProgress: string,
  studyYearLabel: string
) {
  document.getElementById("wlpModal")?.remove();

  const modal = document.createElement("div");
  modal.id = "wlpModal";
  Object.assign(modal.style, {
    display: "flex",
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.6)",
    zIndex: "99999",
    justifyContent: "center",
    alignItems: "center",
  });

  const panel = document.createElement("div");
  Object.assign(panel.style, {
    background: "#f8f9fa",
    borderRadius: "10px",
    width: "92%",
    maxWidth: "1150px",
    padding: "20px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.4)",
    position: "relative",
    maxHeight: "92vh",
    overflowY: "auto",
    fontFamily: "Arial, sans-serif",
  });

  const closeSpan = document.createElement("span");
  closeSpan.innerHTML = "✕ Close";
  Object.assign(closeSpan.style, {
    position: "absolute",
    top: "12px",
    right: "18px",
    fontSize: "16px",
    color: "white",
    cursor: "pointer",
    backgroundColor: "red",
    padding: "5px",
    borderRadius: "4px",
  });
  closeSpan.onclick = () => modal.remove();

  const content = document.createElement("div");
  content.id = "wlpModalContent";
  content.innerHTML = `
    <div style="background:#007a78; color:white; text-align:center; font-weight:bold; padding:10px; font-size:18px; border-radius:6px; margin-bottom:15px;">
      WLP COMPLETION SUMMARY
      <div style="font-size:13px; margin-top:4px;">Study Year : <b>${studyYearLabel}</b></div>
      <div style="font-size:12px; font-weight:normal; margin-top:3px;">Distribution of Students by Weekly Lesson Plan (WLP) Completion</div>
    </div>
    <div style="display:flex; gap:15px; flex-wrap:wrap; align-items:flex-start;">
      <div style="flex:1.2; min-width:280px; text-align:center; background:#fff; padding:10px; border-radius:6px; border:1px solid #ddd; position:relative;">
        <canvas id="wlpPieCanvas" width="280" height="250" style="cursor:pointer;"></canvas>
        <div id="wlpPieTooltip" style="display:none; position:absolute; background:rgba(0,0,0,0.85); color:#fff; padding:8px 12px; border-radius:5px; font-size:12px; pointer-events:none; text-align:left; z-index:100; box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>
      </div>
      <div style="flex:1.5; min-width:400px;">
        <table style="width:100%; border-collapse:collapse; font-size:12px; background:#fff; border:1px solid #ddd; text-align:center;">
          <thead style="background:#007a78; color:white;">
            <tr><th style="padding:6px; text-align:left;">Completion Range</th><th style="padding:6px;">Students</th><th style="padding:6px;">Percentage</th><th style="padding:6px;">Avg Progress</th></tr>
          </thead>
          <tbody>
            ${renderSummaryRow(globalGroups["90"])}
            ${renderSummaryRow(globalGroups["75"])}
            ${renderSummaryRow(globalGroups["50"])}
            ${renderSummaryRow(globalGroups["25"])}
            ${renderSummaryRow(globalGroups["0"])}
          </tbody>
        </table>
      </div>
      <div style="flex:1.8; min-width:340px; background:#fff; padding:10px; border-radius:6px; border:1px solid #ddd; text-align:center; position:relative;">
        <div style="font-size:12px; font-weight:bold; margin-bottom:5px;" id="wlpScatterTitle">Students (100% Completed) – Time vs Progress</div>
        <canvas id="wlpScatterCanvas" width="340" height="300"></canvas>
        <div id="wlpScatterTooltip" style="display:none; position:absolute; background:rgba(0,0,0,0.85); color:#fff; padding:6px 10px; border-radius:4px; font-size:11px; pointer-events:none; text-align:left; z-index:100;"></div>
      </div>
    </div>
    <div style="display:flex; gap:10px; margin:15px 0; flex-wrap:wrap;">
      <div style="flex:1; min-width:130px; background:#ebf3fa; padding:10px; border-radius:6px; text-align:center;">
        <div style="font-size:11px; color:#555;">Total Students</div>
        <div style="font-size:20px; font-weight:bold; color:#1e3d59;">${totalStudents}</div>
      </div>
      <div style="flex:1; min-width:130px; background:#e8f8f5; padding:10px; border-radius:6px; text-align:center;">
        <div style="font-size:11px; color:#555;">Completed (90–100%)</div>
        <div style="font-size:20px; font-weight:bold; color:#27ae60;">${globalGroups["90"].count} <span style="font-size:11px;">(${globalGroups["90"].percent}%)</span></div>
        <div style="font-size:11px; color:#27ae60; font-weight:bold;">Avg Progress: ${formatProgressText(globalGroups["90"].avgProgress || "0")}</div>
      </div>
      <div style="flex:1; min-width:130px; background:#fef9e7; padding:10px; border-radius:6px; text-align:center;">
        <div style="font-size:11px; color:#555;">Partially Completed (1–99%)</div>
        <div style="font-size:20px; font-weight:bold; color:#d35400;">${partialCount} <span style="font-size:11px;">(${((partialCount / totalStudents) * 100).toFixed(2)}%)</span></div>
        <div style="font-size:11px; color:#d35400; font-weight:bold;">Avg Progress: ${formatProgressText(partialAvgProgress)}</div>
      </div>
      <div style="flex:1; min-width:130px; background:#fadbd8; padding:10px; border-radius:6px; text-align:center;">
        <div style="font-size:11px; color:#555;">Not Started / Low (0–24%)</div>
        <div style="font-size:20px; font-weight:bold; color:#c0392b;">${globalGroups["0"].count} <span style="font-size:11px;">(${globalGroups["0"].percent}%)</span></div>
        <div style="font-size:11px; color:#c0392b; font-weight:bold;">Avg Progress: ${formatProgressText(globalGroups["0"].avgProgress || "0")}</div>
      </div>
    </div>
    <div style="background:#fff; padding:12px; border-radius:6px; border:1px solid #ddd;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; flex-wrap:wrap; gap:10px;">
        <div style="font-weight:bold; font-size:14px;" id="wlpTableTitle">👥 90–100% COMPLETED STUDENTS (${globalGroups["90"].count})</div>
        <div style="display:flex; gap:10px;">
          <input type="text" id="wlpSearchBox" placeholder="Search student..." style="padding:4px 8px; font-size:12px; border:1px solid #ccc; border-radius:4px;" />
        </div>
      </div>
      <div style="max-height:220px; overflow-y:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:12px; text-align:center;" id="wlpStudentDetailTable">
          <thead style="background:#007a78; color:white; position:sticky; top:0;">
            <tr>
              <th style="padding:6px;">#</th><th style="padding:6px; text-align:left;">Student Name</th>
              <th style="padding:6px; text-align:left;">Dealer / School</th><th style="padding:6px;">Previous Avg %</th>
              <th style="padding:6px;">Latest Avg %</th><th style="padding:6px;">Progress %</th>
              <th style="padding:6px;">Days Gap</th><th style="padding:6px;">Practice Time</th><th style="padding:6px;">No of WLP</th>
            </tr>
          </thead>
          <tbody id="wlpStudentTableBody"></tbody>
        </table>
      </div>
    </div>
  `;

  panel.appendChild(closeSpan);
  panel.appendChild(content);
  modal.appendChild(panel);
  document.body.appendChild(modal);

  drawWlpPieChart(globalGroups, totalStudents);
  drawWlpScatterPlot(globalGroups["90"].students);
  renderWlpStudentTable(globalGroups["90"].students);
  attachPieCanvasEvents(globalGroups, totalStudents);

  document.getElementById("wlpSearchBox")!.addEventListener("keyup", filterWlpTable);
}

let currentHoveredKey: string | null = null;

function drawWlpPieChart(globalGroups: Record<string, WlpGroup>, total: number, activeKey?: string | null) {
  const canvas = document.getElementById("wlpPieCanvas") as HTMLCanvasElement | null;
  if (!canvas) return;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const baseRadius = Math.min(centerX, centerY) - 20;
  let startAngle = -0.5 * Math.PI;

  Object.keys(globalGroups).forEach((k) => {
    const g = globalGroups[k] as WlpGroup & { startAngle?: number; endAngle?: number };
    if (g.count === 0) return;

    const sliceAngle = (g.count / total) * 2 * Math.PI;
    const endAngle = startAngle + sliceAngle;
    g.startAngle = startAngle;
    g.endAngle = endAngle;

    const isHovered = activeKey === k;
    const currentRadius = isHovered ? baseRadius + 8 : baseRadius;

    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, currentRadius, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = g.color;
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.stroke();

    if (g.count / total > 0.04) {
      const mid = startAngle + sliceAngle / 2;
      const tx = centerX + Math.cos(mid) * (currentRadius * 0.65);
      const ty = centerY + Math.sin(mid) * (currentRadius * 0.65);
      ctx.fillStyle = "#fff";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(g.count), tx, ty);
    }

    startAngle = endAngle;
  });
}

function attachPieCanvasEvents(globalGroups: Record<string, WlpGroup>, totalStudents: number) {
  const canvas = document.getElementById("wlpPieCanvas") as HTMLCanvasElement;
  const tooltip = document.getElementById("wlpPieTooltip")!;

  canvas.onmousemove = (e) => {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const baseRadius = Math.min(centerX, centerY) - 20;

    let hoveredGroup: (WlpGroup & { startAngle?: number; endAngle?: number }) | null = null;

    if (distance <= baseRadius + 10) {
      let angle = Math.atan2(dy, dx);
      if (angle < -0.5 * Math.PI) angle += 2 * Math.PI;

      for (const k of Object.keys(globalGroups)) {
        const g = globalGroups[k] as WlpGroup & { startAngle?: number; endAngle?: number };
        if (g.count === 0) continue;
        if (angle >= (g.startAngle ?? 0) && angle < (g.endAngle ?? 0)) {
          hoveredGroup = g;
          break;
        }
      }
    }

    if (hoveredGroup) {
      if (currentHoveredKey !== hoveredGroup.key) {
        currentHoveredKey = hoveredGroup.key;
        drawWlpPieChart(globalGroups, totalStudents, hoveredGroup.key);
      }
      tooltip.style.display = "block";
      tooltip.style.left = `${x + 15}px`;
      tooltip.style.top = `${y - 10}px`;
      tooltip.innerHTML = `
        <div style="font-weight:bold; color:${hoveredGroup.color}; margin-bottom:3px;">${hoveredGroup.label}</div>
        <div>Students: <b>${hoveredGroup.count}</b> (${hoveredGroup.percent}%)</div>
        <div>Average Progress: <b>${formatProgressText(hoveredGroup.avgProgress || "0")}</b></div>
      `;
    } else {
      if (currentHoveredKey !== null) {
        currentHoveredKey = null;
        drawWlpPieChart(globalGroups, totalStudents, null);
      }
      tooltip.style.display = "none";
    }
  };

  canvas.onmouseleave = () => {
    currentHoveredKey = null;
    drawWlpPieChart(globalGroups, totalStudents, null);
    tooltip.style.display = "none";
  };

  canvas.onclick = () => {
    if (currentHoveredKey && globalGroups[currentHoveredKey]) {
      const g = globalGroups[currentHoveredKey];
      document.getElementById("wlpTableTitle")!.innerHTML = `👥 ${g.label.toUpperCase()} STUDENTS (${g.count})`;
      document.getElementById("wlpScatterTitle")!.innerHTML = `Students (${g.label}) – Time vs Progress`;
      renderWlpStudentTable(g.students);
      drawWlpScatterPlot(g.students);
    }
  };
}

function drawWlpScatterPlot(students: WlpStudent[]) {
  const canvas = document.getElementById("wlpScatterCanvas") as HTMLCanvasElement | null;
  if (!canvas) return;
  const ctx = canvas.getContext("2d")!;
  const tooltip = document.getElementById("wlpScatterTooltip")!;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const w = canvas.width;
  const h = canvas.height;
  const paddingLeft = 45;
  const paddingBottom = 55;
  const paddingTop = 20;
  const paddingRight = 20;
  const chartW = w - paddingLeft - paddingRight;
  const chartH = h - paddingTop - paddingBottom;

  ctx.strokeStyle = "#555";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(paddingLeft, paddingTop);
  ctx.lineTo(paddingLeft, h - paddingBottom);
  ctx.lineTo(w - paddingRight, h - paddingBottom);
  ctx.stroke();

  ctx.fillStyle = "#333";
  ctx.font = "bold 11px Arial";
  ctx.textAlign = "center";
  ctx.fillText("Weekly Practice Time", paddingLeft + chartW / 2, h - 8);

  ctx.save();
  ctx.translate(12, paddingTop + chartH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText("Progress %", 0, 0);
  ctx.restore();

  const zeroY = paddingTop + chartH / 2;
  ctx.strokeStyle = "#ccc";
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(paddingLeft, zeroY);
  ctx.lineTo(w - paddingRight, zeroY);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.fillStyle = "#666";
  ctx.font = "9px Arial";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  ctx.fillText("+50%", paddingLeft - 5, paddingTop + 10);
  ctx.fillText("0%", paddingLeft - 5, zeroY);
  ctx.fillText("-50%", paddingLeft - 5, h - paddingBottom - 10);

  if (!students || students.length === 0) return;

  let maxTime = Math.max(...students.map((s) => s.weeklyPracticeTime)) || 60;
  if (maxTime === 0) maxTime = 60;

  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  const xTicks = 4;
  for (let i = 0; i <= xTicks; i++) {
    const tickVal = (maxTime / xTicks) * i;
    const tickX = paddingLeft + (i / xTicks) * chartW;
    ctx.fillText(formatMinutesToReadable(tickVal), tickX, h - paddingBottom + 5);
  }

  let maxProgressStudent: (WlpStudent & { _cx?: number; _cy?: number }) | null = null;
  students.forEach((s: WlpStudent & { _cx?: number; _cy?: number }) => {
    const cx = paddingLeft + (s.weeklyPracticeTime / maxTime) * chartW;
    const normProgress = Math.max(-50, Math.min(50, s.progress));
    const cy = zeroY - (normProgress / 50) * (chartH / 2 - 10);
    s._cx = cx;
    s._cy = cy;
    if (!maxProgressStudent || s.progress > maxProgressStudent.progress) maxProgressStudent = s;
  });

  if (maxProgressStudent) {
    const originX = paddingLeft;
    const originY = zeroY;
    const mp = maxProgressStudent as WlpStudent & { _cx: number; _cy: number };
    const dx = mp._cx - originX;
    const dy = mp._cy - originY;
    let endX = w - paddingRight;
    let endY = originY;

    if (dx > 0) {
      const slope = dy / dx;
      endY = originY + slope * (endX - originX);
      if (endY < paddingTop) {
        endY = paddingTop;
        endX = originX + (paddingTop - originY) / slope;
      }
    } else {
      endX = originX;
      endY = paddingTop;
    }

    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = "#27ae60";
    ctx.lineWidth = 2;
    ctx.moveTo(originX, originY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  students.forEach((s: WlpStudent & { _cx?: number; _cy?: number }) => {
    ctx.beginPath();
    ctx.arc(s._cx!, s._cy!, 5, 0, 2 * Math.PI);
    ctx.fillStyle = s.progress >= 0 ? "#27ae60" : "#e74c3c";
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    ctx.stroke();
  });

  canvas.onmousemove = (e) => {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let hoveredStudent: (WlpStudent & { _cx?: number; _cy?: number }) | null = null;
    for (const s of students as (WlpStudent & { _cx?: number; _cy?: number })[]) {
      const dist = Math.sqrt((mx - s._cx!) ** 2 + (my - s._cy!) ** 2);
      if (dist <= 6) {
        hoveredStudent = s;
        break;
      }
    }

    if (hoveredStudent) {
      tooltip.style.display = "block";
      tooltip.style.left = `${hoveredStudent._cx! + 10}px`;
      tooltip.style.top = `${hoveredStudent._cy! - 10}px`;
      const formattedTime = formatMinutesToReadable(hoveredStudent.weeklyPracticeTime);
      tooltip.innerHTML = `
        <b>${hoveredStudent.name}</b><br/>
        Practice: <b>${formattedTime}/wk</b><br/>
        Progress: <b style="color:${hoveredStudent.progress >= 0 ? "#27ae60" : "#e74c3c"};">${formatProgressText(hoveredStudent.progress)}</b>
      `;
    } else {
      tooltip.style.display = "none";
    }
  };

  canvas.onmouseleave = () => {
    tooltip.style.display = "none";
  };
}

function renderWlpStudentTable(students: WlpStudent[]) {
  const tbody = document.getElementById("wlpStudentTableBody");
  if (!tbody) return;
  tbody.innerHTML = "";

  if (!students || students.length === 0) {
    tbody.innerHTML = `<tr><td colspan="9" style="padding:15px; color:#888;">No students found in this range.</td></tr>`;
    return;
  }

  students.forEach((s, idx) => {
    const isPositive = s.progress >= 0;
    const progressColor = isPositive ? "#27ae60" : "#e74c3c";
    tbody.innerHTML += `
      <tr style="border-bottom:1px solid #eee;">
        <td style="padding:5px;">${idx + 1}</td>
        <td style="padding:5px; text-align:left; font-weight:bold;">${s.name}</td>
        <td style="padding:5px; text-align:left;">${s.school}</td>
        <td style="padding:5px;">${s.prevAvg}%</td>
        <td style="padding:5px;">${s.latestAvg}%</td>
        <td style="padding:5px; font-weight:bold; color:${progressColor};">${formatProgressText(s.progress)}</td>
        <td style="padding:5px;">${s.daysGap}</td>
        <td style="padding:5px;">${s.practiceText}</td>
        <td style="padding:5px;">${s.wlp}</td>
      </tr>
    `;
  });
}

function filterWlpTable() {
  const input = document.getElementById("wlpSearchBox") as HTMLInputElement;
  const q = input.value.toLowerCase();
  document.querySelectorAll("#wlpStudentTableBody tr").forEach((r) => {
    const el = r as HTMLElement;
    const text = el.innerText.toLowerCase();
    el.style.display = text.indexOf(q) > -1 ? "" : "none";
  });
}